<script lang="ts">
  import Icon from './Icon.svelte';
  /**
   * Block-based body editor for materials. Renders a vertical, drag-reorderable
   * list of blocks (text / image / gallery) with a "+ Додати" button that
   * appends a new empty block of the chosen type.
   *
   * State ownership: BlockEditor keeps a local mirror of `blocks` so dnd-action
   * can do its in-flight item swaps without round-tripping through parent state
   * on every consider event. Only `finalize` (and explicit add/edit/remove)
   * push the change up via onChange. Parent treats this as a controlled
   * component — pass blocks in, get blocks out.
   */
  import { dndzone, type DndEvent } from 'svelte-dnd-action';
  import MarkdownEditor from './MarkdownEditor.svelte';
  import ImageUploader from './ImageUploader.svelte';
  import ConfirmModal from './ConfirmModal.svelte';
  import Lightbox from './Lightbox.svelte';
  import { api } from '../lib/api';
  import type { Block, TextBlock, ImageBlock, GalleryBlock } from '../lib/types';

  /** Flat list of every image URL currently referenced by a block array. */
  function collectImageUrls(blocks: Block[]): Set<string> {
    const seen = new Set<string>();
    for (const b of blocks) {
      if (b.type === 'image' && b.url) seen.add(b.url);
      else if (b.type === 'gallery') for (const u of b.urls) seen.add(u);
    }
    return seen;
  }

  interface Props {
    blocks: Block[];
    onChange: (next: Block[]) => void;
  }
  let { blocks, onChange }: Props = $props();

  // Local source of truth — captured once at mount. Parent owns `blocks` as
  // initial value; from then on we push every change up via onChange and the
  // parent mirrors back through its own $state. We do NOT $effect-resync from
  // the prop because dnd-action mutates items during drag (consider events),
  // and any concurrent prop-driven reset would clobber the in-flight placeholder.
  // BlockEditor is always rendered inside `{#if loadedMaterial}` in our pages,
  // so by the time it mounts the prop already has the real data.
  // svelte-ignore state_referenced_locally — початковий знімок тут навмисний,
  // причина в коментарі вище: ресинк із пропа зіпсував би drag-and-drop.
  let items = $state<Block[]>(blocks);

  let menuOpen = $state(false);

  // Lightbox state — non-null = open. Shared between image/gallery clicks so
  // the admin can preview any image at full size right from the editor.
  let lightbox = $state<{ images: string[]; index: number } | null>(null);
  function openLightbox(images: string[], index: number): void {
    lightbox = { images, index };
  }

  // Tracks which image URLs are currently in the material so we can fire a
  // Bunny delete the moment one disappears from items — without waiting for
  // the parent save. Initialised from the prop and updated whenever items
  // mutates.
  // svelte-ignore state_referenced_locally — теж знімок на момент монтування;
  // далі набір оновлює syncImageDeletions() з локальних items.
  let trackedImageUrls = collectImageUrls(blocks);

  /** Diff trackedImageUrls against the current items, fire delete for each
   *  url that vanished, and refresh the tracking set. Idempotent on Bunny
   *  side (404 == success), so concurrent calls aren't a problem. */
  function syncImageDeletions(): void {
    const current = collectImageUrls(items);
    for (const url of trackedImageUrls) {
      if (!current.has(url)) {
        api.adminDeleteImage(url).catch(() => {
          // Best-effort. The material PATCH cleanup path is still in place
          // as a fallback, and Bunny 404 doesn't matter since deletes are
          // idempotent. We swallow the error rather than blocking the UI.
        });
      }
    }
    trackedImageUrls = current;
  }

  function newId(): string {
    return crypto.randomUUID();
  }

  function addBlock(type: Block['type']): void {
    const id = newId();
    const block: Block =
      type === 'text'
        ? { id, type, markdown: '' }
        : type === 'image'
          ? { id, type, url: '' }
          : { id, type, urls: [] };
    items = [...items, block];
    onChange(items);
    menuOpen = false;
  }

  function updateBlock(id: string, patch: Partial<Block>): void {
    items = items.map((b) => (b.id === id ? ({ ...b, ...patch } as Block) : b));
    syncImageDeletions();
    onChange(items);
  }

  function removeBlock(id: string): void {
    items = items.filter((b) => b.id !== id);
    syncImageDeletions();
    onChange(items);
  }

  /** A block is "empty" if the admin hasn't put anything into it yet — fresh
   *  blocks from "+ Додати" land here. Empty blocks delete silently because
   *  there's nothing to lose; only blocks with content trigger the modal. */
  function isBlockEmpty(b: Block): boolean {
    if (b.type === 'text') return b.markdown.trim().length === 0;
    if (b.type === 'image') return !b.url;
    return b.urls.length === 0;
  }

  /** Entry point for the × button — either deletes silently (empty block) or
   *  opens the confirmation modal (block has content). */
  function requestRemove(id: string): void {
    const block = items.find((b) => b.id === id);
    if (!block || isBlockEmpty(block)) {
      removeBlock(id);
      return;
    }
    pendingRemoveId = id;
  }

  // Two-step removal: click × → store pending id and open the modal, then
  // either commit (calls removeBlock) or dismiss (clears pending). One state
  // var doubles as both the modal-open flag and the target id.
  let pendingRemoveId = $state<string | null>(null);
  const pendingBlock = $derived(
    pendingRemoveId ? items.find((b) => b.id === pendingRemoveId) ?? null : null,
  );
  function confirmRemove(): void {
    if (pendingRemoveId) removeBlock(pendingRemoveId);
    pendingRemoveId = null;
  }
  function blockLabel(b: Block | null): string {
    if (!b) return 'блок';
    if (b.type === 'text') return 'текстовий блок';
    if (b.type === 'image') return 'блок з картинкою';
    return 'медіа-групу';
  }

  function onConsider(e: CustomEvent<DndEvent<Block>>): void {
    items = e.detail.items;
  }
  function onFinalize(e: CustomEvent<DndEvent<Block>>): void {
    items = e.detail.items;
    onChange(items);
  }

  // Type narrowing helpers for the template — Svelte 5's discriminated unions
  // need a typed bridge for type-block-specific props.
  function asText(b: Block): TextBlock {
    return b as TextBlock;
  }
  function asImage(b: Block): ImageBlock {
    return b as ImageBlock;
  }
  function asGallery(b: Block): GalleryBlock {
    return b as GalleryBlock;
  }
</script>

<div class="blocks">
  <section
    class="blocks-zone"
    use:dndzone={{
      items,
      dragDisabled: false,
      flipDurationMs: 180,
      type: 'material-blocks',
      dropTargetStyle: {},
    }}
    onconsider={onConsider}
    onfinalize={onFinalize}
  >
    {#each items as block (block.id)}
      <div class="block">
        <div class="block-head">
          <div class="drag-handle" aria-label="Перетягнути">
            <svg width="10" height="16" viewBox="0 0 6 10" fill="currentColor" aria-hidden="true">
              <circle cx="1" cy="1" r="1" /><circle cx="5" cy="1" r="1" />
              <circle cx="1" cy="5" r="1" /><circle cx="5" cy="5" r="1" />
              <circle cx="1" cy="9" r="1" /><circle cx="5" cy="9" r="1" />
            </svg>
          </div>
          <div class="block-type-label">
            {#if block.type === 'text'}<Icon name="file-text" size="xs" /> Текст
            {:else if block.type === 'image'}<Icon name="image" size="xs" /> Картинка
            {:else}<Icon name="image" size="xs" /> Медіа-група
            {/if}
          </div>
          <button
            type="button"
            class="block-remove"
            onclick={() => requestRemove(block.id)}
            aria-label="Видалити блок"
            title="Видалити блок"
          >×</button>
        </div>
        <div class="block-body">
          {#if block.type === 'text'}
            {@const tb = asText(block)}
            <MarkdownEditor
              value={tb.markdown}
              maxlength={20000}
              onChange={(v: string) => updateBlock(block.id, { markdown: v })}
            />
          {:else if block.type === 'image'}
            {@const ib = asImage(block)}
            <ImageUploader
              mode="single"
              value={ib.url || null}
              onChange={(v) => updateBlock(block.id, { url: v ?? '' })}
              previewWidth={ib.width ?? 'full'}
              onImageClick={(url) => openLightbox([url], 0)}
            />
            {#if ib.url}
              <div class="size-row">
                <span class="size-label">Розмір:</span>
                {#each [{ v: 'full', l: 'Повна' }, { v: 'medium', l: 'Середня' }, { v: 'small', l: 'Мала' }] as opt (opt.v)}
                  <button
                    type="button"
                    class="size-btn"
                    class:active={(ib.width ?? 'full') === opt.v}
                    onclick={() => updateBlock(block.id, { width: opt.v as 'full' | 'medium' | 'small' })}
                  >{opt.l}</button>
                {/each}
              </div>
            {/if}
          {:else}
            {@const gb = asGallery(block)}
            {@const effectiveCols = gb.cols && gb.urls.length >= gb.cols ? gb.cols : 2}
            <ImageUploader
              mode="group"
              value={gb.urls}
              onChange={(v) => updateBlock(block.id, { urls: v })}
              max={10}
              onImageClick={(_url, i) => openLightbox(gb.urls, i)}
              cols={gb.urls.length > 0 ? effectiveCols : undefined}
            />
            {#if gb.urls.length > 0}
              <div class="size-row">
                <span class="size-label">У рядку:</span>
                {#each [2, 3, 4] as n (n)}
                  <button
                    type="button"
                    class="size-btn"
                    class:active={(gb.cols ?? 2) === n}
                    disabled={gb.urls.length < n}
                    title={gb.urls.length < n ? `Потрібно ${n}+ картинок` : ''}
                    onclick={() => updateBlock(block.id, { cols: n as 2 | 3 | 4 })}
                  >{n}</button>
                {/each}
              </div>
            {/if}
          {/if}
        </div>
      </div>
    {/each}
  </section>

  <div class="add-wrap">
    <button
      type="button"
      class="add-btn"
      class:open={menuOpen}
      onclick={() => (menuOpen = !menuOpen)}
    >
      + Додати блок
    </button>
    {#if menuOpen}
      <div class="add-menu" role="menu">
        <button type="button" class="add-option" onclick={() => addBlock('text')}>
          <span class="emoji"><Icon name="file-text" size="lg" /></span>
          <div class="add-option-text">
            <div class="add-option-title">Текст</div>
            <div class="add-option-desc">Markdown-абзац</div>
          </div>
        </button>
        <button type="button" class="add-option" onclick={() => addBlock('image')}>
          <span class="emoji"><Icon name="image" size="lg" /></span>
          <div class="add-option-text">
            <div class="add-option-title">Картинка</div>
            <div class="add-option-desc">Одне зображення на всю ширину</div>
          </div>
        </button>
        <button type="button" class="add-option" onclick={() => addBlock('gallery')}>
          <span class="emoji"><Icon name="image" size="lg" /></span>
          <div class="add-option-text">
            <div class="add-option-title">Медіа-група</div>
            <div class="add-option-desc">До 10 зображень у сітці</div>
          </div>
        </button>
      </div>
    {/if}
  </div>
</div>

<ConfirmModal
  open={pendingRemoveId !== null}
  title="Видалити блок?"
  message={`Прибрати ${blockLabel(pendingBlock)} з матеріалу. Картинки одразу видаляться з Bunny — повернути їх не можна.`}
  confirmLabel="Так, видалити"
  cancelLabel="Скасувати"
  onConfirm={confirmRemove}
  onCancel={() => (pendingRemoveId = null)}
/>

{#if lightbox}
  <Lightbox
    images={lightbox.images}
    initialIndex={lightbox.index}
    onClose={() => (lightbox = null)}
  />
{/if}

<style>
  .blocks {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 14px;
  }
  .blocks-zone {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .block {
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
    overflow: hidden;
  }
  .block-head {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    background: var(--bg);
    border-bottom: 1px solid var(--border);
  }
  .drag-handle {
    cursor: grab;
    color: var(--text-muted);
    padding: 4px;
    border-radius: 4px;
    display: flex;
    align-items: center;
  }
  .drag-handle:hover {
    background: var(--border);
    color: var(--text);
  }
  .drag-handle:active {
    cursor: grabbing;
  }
  .block-type-label {
    flex: 1;
    font-size: 12px;
    font-weight: 600;
    color: var(--text-muted);
    letter-spacing: 0.3px;
  }
  .block-remove {
    background: transparent;
    border: none;
    color: var(--text-muted);
    font-size: 20px;
    line-height: 1;
    padding: 4px 8px;
    cursor: pointer;
    font-family: inherit;
    border-radius: 4px;
    transition: all 0.15s;
  }
  .block-remove:hover {
    background: rgba(196, 99, 62, 0.12);
    color: var(--heart);
  }
  .block-body {
    padding: 12px;
  }
  /* Inside-block components don't need their own outer margin */
  .block-body :global(.uploader) {
    margin-bottom: 0;
  }

  /* Size picker shown only for image blocks after upload */
  .size-row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    flex-wrap: wrap;
  }
  .size-label {
    font-size: 11px;
    color: var(--text-muted);
    margin-right: 4px;
    font-weight: 500;
  }
  .size-btn {
    background: var(--bg);
    border: 1px solid var(--border);
    color: var(--text);
    padding: 5px 11px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.15s;
  }
  .size-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .size-btn:disabled:hover {
    border-color: var(--border);
    color: var(--text);
  }
  .size-btn:hover:not(:disabled) {
    border-color: var(--outline);
    color: var(--accent-text);
  }
  .size-btn.active {
    background: var(--pu);
    border-color: var(--pu);
    color: var(--text-light);
  }

  /* Add block button + menu */
  .add-wrap {
    position: relative;
  }
  .add-btn {
    width: 100%;
    background: var(--bg2);
    border: 1.5px dashed var(--border2);
    color: var(--text-muted);
    padding: 14px;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.15s;
  }
  .add-btn:hover,
  .add-btn.open {
    border-color: var(--outline);
    color: var(--accent-text);
    background: var(--pu-light);
  }
  .add-menu {
    margin-top: 8px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
  .add-option {
    width: 100%;
    background: transparent;
    border: none;
    border-bottom: 1px solid var(--border);
    padding: 12px 14px;
    cursor: pointer;
    font-family: inherit;
    display: flex;
    align-items: center;
    gap: 12px;
    text-align: left;
    transition: background 0.15s;
  }
  .add-option:last-child {
    border-bottom: none;
  }
  .add-option:hover {
    background: var(--pu-light);
  }
  .add-option .emoji {
    font-size: 20px;
    flex-shrink: 0;
  }
  .add-option-text {
    flex: 1;
    min-width: 0;
  }
  .add-option-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
  }
  .add-option-desc {
    font-size: 11px;
    color: var(--text-muted);
    margin-top: 1px;
  }
</style>
